import { useState, useEffect, useRef } from 'react';
import { Search, Sparkles, Clock, Users, Activity, FileText, Target, TrendingUp, X } from 'lucide-react';

interface Command {
  id: string;
  title: string;
  description: string;
  icon: any;
  category: string;
  action: () => void;
}

interface AICommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AICommandPalette({ isOpen, onClose }: AICommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands: Command[] = [
    {
      id: 'analyze-member',
      title: 'Analyze member risk',
      description: 'AI analyzes member health data and predicts risk factors',
      icon: TrendingUp,
      category: 'AI Actions',
      action: () => console.log('Analyzing member risk')
    },
    {
      id: 'generate-careplan',
      title: 'Generate care plan',
      description: 'AI creates personalized care plan based on member data',
      icon: FileText,
      category: 'AI Actions',
      action: () => console.log('Generating care plan')
    },
    {
      id: 'find-gaps',
      title: 'Find care gaps',
      description: 'AI identifies quality measure gaps across population',
      icon: Target,
      category: 'AI Actions',
      action: () => console.log('Finding care gaps')
    },
    {
      id: 'predict-readmission',
      title: 'Predict readmissions',
      description: 'AI forecasts which members are at risk of readmission',
      icon: Activity,
      category: 'AI Actions',
      action: () => console.log('Predicting readmissions')
    },
    {
      id: 'optimize-outreach',
      title: 'Optimize outreach',
      description: 'AI determines best time and channel for member engagement',
      icon: Users,
      category: 'AI Actions',
      action: () => console.log('Optimizing outreach')
    },
  ];

  const filteredCommands = query
    ? commands.filter(cmd =>
      cmd.title.toLowerCase().includes(query.toLowerCase()) ||
      cmd.description.toLowerCase().includes(query.toLowerCase())
    )
    : commands;

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.length > 3) {
      setIsThinking(true);
      const timer = setTimeout(() => {
        setAiSuggestion(`AI suggests: "${query}" - I can help you with that. Try "Analyze member risk" or "Generate care plan"`);
        setIsThinking(false);
      }, 800);
      return () => clearTimeout(timer);
    } else {
      setAiSuggestion('');
    }
  }, [query]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
        <div className="relative">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <Sparkles className="w-5 h-5 text-blue-600 animate-pulse" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask AI anything... (e.g., 'analyze high-risk members', 'generate care plan')"
              className="flex-1 bg-transparent border-none outline-none text-lg placeholder:text-gray-400 pl-2"
            />
            {isThinking && (
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            )}
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {aiSuggestion && (
            <div className="px-6 py-3 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-blue-100">
              <div className="flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-900">{aiSuggestion}</p>
              </div>
            </div>
          )}

          <div className="max-h-[60vh] overflow-y-auto">
            {filteredCommands.length > 0 ? (
              <div className="py-2">
                {filteredCommands.map((command) => {
                  const Icon = command.icon;
                  return (
                    <button
                      key={command.id}
                      onClick={() => {
                        command.action();
                        onClose();
                      }}
                      className="w-full px-6 py-3 flex items-start gap-4 hover:bg-gray-50 transition-colors text-left group"
                    >
                      <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg group-hover:scale-110 transition-transform">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                          {command.title}
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5">
                          {command.description}
                        </div>
                      </div>
                      <div className="flex-shrink-0 px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                        AI
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="py-12 text-center">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No commands found</p>
                <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
              </div>
            )}
          </div>

          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-white border border-gray-300 rounded shadow-sm">↑</kbd>
                <kbd className="px-2 py-1 bg-white border border-gray-300 rounded shadow-sm">↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-white border border-gray-300 rounded shadow-sm">Enter</kbd>
                Execute
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-white border border-gray-300 rounded shadow-sm">Esc</kbd>
                Close
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-blue-600" />
              <span>Powered by AI</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
